<template>
  <ui-collapse
    class="destinations-collapse"
    :collapse-id="`collapse-low-results-all-countries`"
  >
    <template #ui-collapse-title>
      <translate tag="div">
        Start again by picking one of these wonderful destinations
      </translate>
    </template>
    <template #ui-collapse-body>
      <div>
        <div class="countries-list" :class="{ ready: isReady }">
          <template v-for="country in countryList" :key="country.path">
            <pu-low-results-search-link
              v-if="!!counts[country.path]"
              :modify-filters="{ dates: {}, priceRange: {}, categoryIds: [] }"
              exclude-type="all"
              :hierarchy-path="country.path"
            >
              {{ country.name }}
              <span class="count">({{ counts[country.path] }})</span>
            </pu-low-results-search-link>
          </template>
        </div>
      </div>
    </template>
  </ui-collapse>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import { useNuxtApp } from 'nuxt/app'
import { getHierarchyRepository } from '~/apps/hierarchies/inversify.config'
import { getSearchCountService } from '~/apps/search/infrastructure/SearchCountsService'
import type { Hierarchy } from '../../../hierarchies/domain/types'

const route = useRoute()
const { $sentry } = useNuxtApp()

// State
const isReady = ref(false)
const countryList = ref<Hierarchy[]>([])
const counts = ref<Record<string, number>>({})

// Mount tracking to prevent state updates after unmount
const isMounted = ref(false)

const langCode = computed(() => (route.params.lang as string) || 'en-gb')

async function fetchCountryList() {
  try {
    const countries = await getHierarchyRepository().getCountries(
      langCode.value,
    )
    // Guard: Don't update state if unmounted
    if (!isMounted.value) return
    countryList.value = countries
  } catch (error) {
    // Guard: Don't report if unmounted (likely cancelled/navigation)
    if (!isMounted.value) return
    $sentry.captureException(error)
  }
}

async function fetchFacetCounts() {
  try {
    const results = await getSearchCountService().fetchNonPriceSearchCounts(
      langCode.value,
    )
    // Guard: Don't update state if unmounted
    if (!isMounted.value) return
    counts.value = results?.hierarchyCounts || {}
  } catch {
    // Guard: Don't report if unmounted (likely cancelled/navigation)
    // Silent fail - counts are optional enhancement
    if (!isMounted.value) return
  }
}

onMounted(async () => {
  isMounted.value = true

  // Fetch both in parallel
  await Promise.all([fetchCountryList(), fetchFacetCounts()])

  // Guard: Don't update state if unmounted during fetch
  if (!isMounted.value) return
  isReady.value = true
})

onBeforeUnmount(() => {
  // Set flag immediately to stop all pending async callbacks
  isMounted.value = false
})
</script>

<style scoped>
.count {
  font-size: var(--body);
  color: #826f35;
  align-self: center;
  margin-left: 0.25em;
}

.countries-list {
  padding: 1em 0;
  display: none;
}

.countries-list.ready {
  display: inherit;
}

.countries-list a {
  display: inline-flex;
  align-content: center;
  flex-direction: row;
  margin: 0 0.35em 0.5em 0;
  font-size: var(--small);
}

.destinations-collapse {
  border-bottom-color: #e6d8a9;
  border-top: 1px solid #e6d8a9;
  margin-top: 1em;
}

.destinations-collapse :deep(.ui-collapse-title) {
  font-weight: normal;
}

.destinations-collapse :deep(.ui-collapse-header) {
  padding: 0.5em 0;
  min-height: 3.5em;
}
</style>
